"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import useTracking from "@/lib/startrack/hooks/useTracking";
import { StartrackingEventsNames } from "@/lib/startrack/trackingEvents";
import { useFilterState } from "@/lib/Providers/FilterStateProvider/FilterStateProvider";
import {
  isDeselection,
  getCategoryMapping,
  processCategoryChanges,
  processRoomTypeChanges,
  processTourOperatorChanges,
  processAirlineChanges,
} from "@/lib/Helpers/filterTrackingUtils";
import initialState from "@/lib/StaticData/searchFormData";
import { AppConfig } from "@/lib/Types/AppConfig";
import { getFreshClickId } from "@/lib/Helpers/getFreshClickId";

const getFilterValue = (
  key: string,
  value: string,
  isDeselect = false
): string => {
  switch (key) {
    case "categories": {
      return isDeselect ? "deselected" : "selected";
    }
    case "roomType": {
      const roomTypeId = Number(value);
      return (
        initialState.roomType.byId[
          roomTypeId as keyof typeof initialState.roomType.byId
        ]?.label || value
      );
    }
    case "mealType": {
      const mealTypeId = Number(value);
      return isDeselect
        ? "beliebig"
        : initialState.mealType.byId[
            mealTypeId as keyof typeof initialState.mealType.byId
          ]?.label || value;
    }
    case "stars":
      return isDeselect ? "deselected" : value;
    case "minRecommendation":
      return isDeselect ? "deselected" : value;
    case "price":
      return isDeselect ? "deselected" : value;
    case "flexibleOffers":
      return value === "flexibleCancellation" ? "selected" : "deselected";
    case "specialDeals":
    case "bookOnOption":
    case "oceanView":
      return value === "true" ? "selected" : "deselected";
    default:
      return isDeselect ? "deselected" : value;
  }
};

export function useStartrackFilterTracking(appConfig: AppConfig): void {
  const { track } = useTracking(appConfig);
  const { subscribeToFilterChanges } = useFilterState();
  const pathname = usePathname();

  // Get context for tracking
  const context = pathname.includes("/hotel/") ? "DETAIL" : "RESULTS";

  useEffect(() => {
    const unsubscribe = subscribeToFilterChanges(
      (changedParams, removedParams, currentParams, prevParams) => {
        // Get searchId for tracking
        const searchId = currentParams.searchId ?? undefined;
        // Read clickId fresh from the URL — on detail pages createAndAppendClickId
        // uses silent replaceState that useReactiveSearchParams ignores;
        // on result pages clickId is absent from the URL so this returns "".
        const clickId = getFreshClickId();

        // Handle category changes separately as they're comma-separated arrays
        if (
          changedParams.some(([key]) => key === "categories") ||
          removedParams.some(([key]) => key === "categories")
        ) {
          processCategoryChanges(
            currentParams,
            prevParams,
            (categoryId, isDeselected) => {
              const filterValue = getFilterValue(
                "categories",
                categoryId,
                isDeselected
              );
              const categoryMapping = getCategoryMapping(Number(categoryId));
              if (categoryMapping) {
                track(StartrackingEventsNames.FilterApplied, {
                  filterValue,
                  filterName: categoryMapping,
                  deselectFlag: isDeselected,
                  searchId,
                  context,
                  clickId,
                });
              }
            }
          );
        }

        // Handle roomType changes
        if (
          changedParams.some(([key]) => key === "roomType") ||
          removedParams.some(([key]) => key === "roomType")
        ) {
          processRoomTypeChanges(
            currentParams,
            prevParams,
            (roomTypeId, isDeselected) => {
              const filterValue = getFilterValue("roomType", roomTypeId);
              track(StartrackingEventsNames.FilterApplied, {
                filterValue,
                filterName: "roomType",
                deselectFlag: isDeselected,
                searchId,
                context,
                clickId,
              });
            }
          );
        }

        // Handle tourOperator changes
        if (
          changedParams.some(([key]) => key === "tourOperator") ||
          removedParams.some(([key]) => key === "tourOperator")
        ) {
          processTourOperatorChanges(
            currentParams,
            prevParams,
            (operatorId, isDeselected) => {
              track(StartrackingEventsNames.FilterApplied, {
                filterValue: operatorId,
                filterName: "tourOperators",
                deselectFlag: isDeselected,
                searchId,
                context,
                clickId,
              });
            }
          );
        }

        // Handle airline changes
        if (
          changedParams.some(([key]) => key === "airline") ||
          removedParams.some(([key]) => key === "airline")
        ) {
          processAirlineChanges(
            currentParams,
            prevParams,
            (airlineCode, isDeselected) => {
              track(StartrackingEventsNames.FilterApplied, {
                filterValue: airlineCode,
                filterName: "airlines",
                deselectFlag: isDeselected,
                searchId,
                context,
                clickId,
              });
            }
          );
        }

        // Handle all other filter changes
        [...changedParams, ...removedParams]
          .filter(
            ([key]) =>
              key !== "categories" &&
              key !== "roomType" &&
              key !== "tourOperator" &&
              key !== "airline"
          )
          .forEach(([key]) => {
            const value = currentParams[key]; // will be undefined for removed params
            const prevValue = prevParams[key];
            const deselected = isDeselection(key, value, prevValue);

            const filterValue = value
              ? getFilterValue(key, value, deselected)
              : prevValue
              ? getFilterValue(key, prevValue, deselected)
              : "";

            track(StartrackingEventsNames.FilterApplied, {
              filterValue,
              filterName: key,
              deselectFlag: deselected,
              searchId,
              context,
              clickId,
            });
          });
      }
    );

    return unsubscribe;
  }, [track, context, subscribeToFilterChanges]);
}
